import {
  Button,
  FormControl,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import GKModal from "components/GKModal";
import GKSearchSelect from "components/GKSearchSelect";
import GKTextArea from "components/GKTextarea";
import GKTextField from "components/GKTextField";
import { Controller, useForm } from "react-hook-form";
import { MdDelete } from "react-icons/md";
import axiosInstance from "utils/axiosInstance";
import { theme } from "utils/Theme";

const CreateFaq = (props) => {
  const { inCardOpen, setIsCardOpen, refetch, cardData } = props;

  const { data } = useQuery({
    queryKey: ["GET-FAQ-MODULE"],
    queryFn: () => axiosInstance.get("/faq-modules/"),
    onError: (error) => {
      console.error("Error fetching FAQ modules:", error);
    },
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      question: cardData?.question || "",
      description: cardData?.description || "",
      media_file: cardData?.mediaFiles || [],
      faq_module_id: cardData?.moduleName || [],
    },
  });

  const { mutate: handleCreateFaqDetails } = useMutation({
    mutationKey: ["CREATE_FAQ_DETAILS"],
    mutationFn: (data) => axiosInstance.post("/faq/", data),
    onSuccess: () => {
      refetch();
      setIsCardOpen(false);
    },
    onError: (error) => {
      console.error("Error creating FAQ details:", error);
    },
  });

  const { mutate: handleUpdateFaqDetails } = useMutation({
    mutationKey: ["UPDATE_FAQ_DETAILS"],
    mutationFn: (data) => axiosInstance.put(`/faq/${cardData.id}/`, data),
    onSuccess: () => {
      refetch();
      setIsCardOpen(false);
    },
    onError: (error) => {
      console.error("Error updating FAQ details:", error);
    },
  });

  const onSubmit = async (values) => {
    const faqData = new FormData();
    faqData.append("question", values.question);
    faqData.append("description", values.description);
    faqData.append(
      "faq_module_id",
      JSON.stringify(values.faq_module_id.map((item) => item.id))
    );

    if (values.media_file && values.media_file.length > 0) {
      faqData.append("media_file", values.media_file[0]);
    }

    if (cardData?.id) {
      handleUpdateFaqDetails(faqData);
    } else {
      handleCreateFaqDetails(faqData);
    }
  };

  return (
    <GKModal
      open={inCardOpen}
      setOpen={setIsCardOpen}
      modalTitle={cardData?.id ? "Update FAQ Details" : "Add New FAQ Details"}
      footer={
        <Grid container justifyContent="flex-end" gap={1} marginTop={2}>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
            variant="outlined"
            onClick={() => setIsCardOpen(false)}
          >
            Close
          </Button>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
            type="submit"
            form="create-FAQ-form"
            variant="contained"
          >
            {cardData?.id ? "Update" : "Submit"}
          </Button>
        </Grid>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} id="create-FAQ-form">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="faq_module_id"
                inputLabel="FAQ Modules"
                control={control}
                rules={{ required: "FAQ Module is required" }}
                render={({ field }) => (
                  <GKSearchSelect
                    multiple
                    options={data?.data.data || []}
                    getOptionLabel={(option) => option.moduleName || option}
                    renderInput={(params) => (
                      <GKTextField
                        {...params}
                        inputLabel="FAQ Modules"
                        placeholder="Select"
                      />
                    )}
                    onChange={(e, newValue) => {
                      setValue("faq_module_id", newValue);
                      field.onChange(newValue);
                    }}
                    value={field.value}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="question"
              control={control}
              rules={{ required: "Question is required" }}
              render={({ field }) => (
                <FormControl variant="standard" fullWidth>
                  <GKTextField
                    {...field}
                    error={!!errors.question}
                    helperText={errors.question?.message}
                    inputLabel="question"
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              rules={{ required: "Description is required" }}
              render={({ field }) => (
                <>
                  <GKTextArea
                    minRows={5}
                    inputLabel="Description"
                    {...field}
                    style={{ width: "97%" }}
                  />
                  {errors.description && (
                    <Typography color="error" variant="body2">
                      {errors.description.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="media_file"
              control={control}
              render={({ field }) => (
                <>
                  {field.value.length > 0 ? (
                    <Grid item xs={12}>
                      {Array.from(field.value).map((file, index) => (
                        <Grid
                          container
                          border={"solid 1px "}
                          borderColor={theme.palette.border.main}
                          borderRadius={2}
                          p={1}
                          my={1}
                          key={file.name}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            fontWeight={600}
                            fontSize={12}
                            width={"30%"}
                            textOverflow={"ellipsis"}
                            overflow={"hidden"}
                          >
                            {file.name || file.mediaFileName}
                          </Typography>
                          <IconButton
                            color="error"
                            onClick={() => {
                              const newFiles = Array.from(field.value);
                              newFiles.splice(index, 1);
                              setValue("media_file", newFiles);
                              field.onChange(newFiles);
                            }}
                          >
                            <MdDelete />
                          </IconButton>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Button
                      sx={{
                        fontSize: "0.7rem",
                        fontFamily: '"Arial", sans-serif',
                        textTransform: "none",
                      }}
                      component="label"
                      variant="contained"
                    >
                      Upload File
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          setValue("media_file", e.target.files);
                          field.onChange(e.target.files);
                        }}
                      />
                    </Button>
                  )}
                </>
              )}
            />
          </Grid>
        </Grid>
      </form>
    </GKModal>
  );
};

export default CreateFaq;
